<template>
	<v-layout fill-height wrap>
		<v-flex md12>
			<FormsList
				:forms="formsList"
				:is-loading="isListing"
				@createForm="openCreateBuilder"
				@editForm="openEditBuilder"
				@assignForm="openAssignDialog"
				@archiveForm="archiveForm"
				@unarchiveForm="unarchiveForm"
			/>
		</v-flex>

		<embedded-forms-app-dialog
			v-if="embeddedBuilder.dialog"
			:dialog="embeddedBuilder.dialog"
			:title="embeddedBuilder.title"
			:path="embeddedBuilder.path"
			:query="embeddedBuilder.query"
			@close="closeBuilder"
			@ready="onBuilderReady"
			@routeChanged="onBuilderRouteChanged"
			@authRequired="onBuilderAuthRequired"
			@authLoginStarted="onBuilderAuthStarted"
			@authLoginCompleted="onBuilderAuthCompleted"
			@authLoginFailed="onBuilderAuthFailed"
			@builderSaved="onBuilderSaved"
			@builderPublished="onBuilderPublished"
			@builderCloseRequested="closeBuilder"
		/>

		<form-assignment-dialog
			v-if="assignmentDialog.dialog"
			:dialog="assignmentDialog.dialog"
			:title="assignmentDialogTitle"
			:employees="employees"
			:fixed-form-id="assignmentDialog.form ? assignmentDialog.form.id : null"
			:fixed-form-name="assignmentDialog.form ? assignmentDialog.form.name : ''"
			:is-assigning="isAssigningForm"
			@assign="assignFormFromManagement"
			@close="closeAssignmentDialog"
		/>
	</v-layout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import FormsList from '@/components/forms/FormsList.vue';
import EmbeddedFormsAppDialog from '@/components/forms/EmbeddedFormsAppDialog.vue';
import FormAssignmentDialog from '@/components/forms/FormAssignmentDialog.vue';
import toast from '@/services/toasts/index.js';

export default {
	name: 'PageForms',
	components: {
		FormsList,
		EmbeddedFormsAppDialog,
		FormAssignmentDialog
	},
	data() {
		return {
			embeddedBuilder: {
				dialog: false,
				title: 'Create Form',
				path: '',
				query: {}
			},
			assignmentDialog: {
				dialog: false,
				form: null
			}
		};
	},
	computed: {
		...mapGetters({
			loggedInTenant: 'loggedInTenant',
			formsList: 'forms/formsList',
			isListing: 'forms/isListing',
			isAssigningForm: 'forms/isCreatingSubmission',
			employees: 'employees/employeesMiniActiveOnly'
		}),
		assignmentDialogTitle() {
			const formName = this.assignmentDialog.form && this.assignmentDialog.form.name
				? this.assignmentDialog.form.name
				: 'form';
			return `Assign ${formName}`;
		}
	},
	methods: {
		...mapActions({
			listForms: 'forms/list',
			archiveFormAction: 'forms/archive',
			unarchiveFormAction: 'forms/unarchive',
			createSubmission: 'forms/createSubmission',
			initializeEmployees: 'employees/initialize'
		}),
		openCreateBuilder() {
			if (!this.loggedInTenant || !this.loggedInTenant.id) return;

			this.embeddedBuilder = {
				dialog: true,
				title: 'Create Form',
				path: `/internal/forms/${this.loggedInTenant.id}/builder/start`,
				query: {}
			};
		},
		openEditBuilder(form) {
			if (!this.loggedInTenant || !this.loggedInTenant.id || !form || !form.id) return;

			this.embeddedBuilder = {
				dialog: true,
				title: `Edit ${form.name || 'Form'}`,
				path: `/internal/forms/${this.loggedInTenant.id}/builder/${form.id}`,
				query: {}
			};
		},
		async archiveForm(form) {
			if (!form || !form.id) return;
			const confirmed = window.confirm(`Archive ${form.name || 'this form'}? It will no longer be available for new assignments.`);
			if (!confirmed) return;

			await this.archiveFormAction(form.id);
		},
		async unarchiveForm(form) {
			if (!form || !form.id) return;
			const confirmed = window.confirm(`Unarchive ${form.name || 'this form'}? It will become available for new assignments.`);
			if (!confirmed) return;

			await this.unarchiveFormAction(form.id);
		},
		async openAssignDialog(form) {
			if (!form || form.status !== 'Active') return;

			await this.initializeEmployees();

			this.assignmentDialog = {
				dialog: true,
				form
			};
		},
		closeAssignmentDialog() {
			this.assignmentDialog = {
				dialog: false,
				form: null
			};
		},
		getManagementAssignmentRecipientEmployeeIds(assignment) {
			if (!assignment || typeof assignment !== 'object') return [];

			const employeeId = assignment.employeeId;
			const selectedEmployee = this.employees.find(employee => employee && employee.id === employeeId);
			const ids = [];

			if (assignment.notifyManager && selectedEmployee && selectedEmployee.reportsTo) {
				ids.push(selectedEmployee.reportsTo);
			}

			if (Array.isArray(assignment.notifyEmployeeIds)) {
				ids.push(...assignment.notifyEmployeeIds);
			}

			return [...new Set(ids.filter(id => id && id !== employeeId))];
		},
		async assignFormFromManagement(assignment) {
			if (!assignment || !assignment.formId || !assignment.employeeId) return;

			const employeePartyKind = 1;
			const recipientEmployeeIds = this.getManagementAssignmentRecipientEmployeeIds(assignment);

			const result = await this.createSubmission({
				formId: assignment.formId,
				values: {},
				successMessage: 'Form assigned successfully.',
				errorMessage: 'Failed to assign form.',
				parties: {
					subject: {
						kind: employeePartyKind,
						employeeId: assignment.employeeId
					},
					respondent: {
						kind: employeePartyKind,
						employeeId: assignment.employeeId
					},
					recipients: recipientEmployeeIds.map(employeeId => ({
						kind: employeePartyKind,
						employeeId
					}))
				}
			});

			if (!result.isSuccess) return;

			this.closeAssignmentDialog();
		},
		closeBuilder() {
			this.embeddedBuilder.dialog = false;
		},
		onBuilderReady() {
			this.$store.dispatch('setPageTitle', 'Forms');
		},
		onBuilderRouteChanged() {
			this.$store.dispatch('setPageTitle', 'Forms');
		},
		onBuilderAuthStarted() {
			// Embedded builder shows its own auth overlay.
		},
		onBuilderAuthRequired() {
			// Child app will attempt silent SSO first and fall back to popup if required.
		},
		onBuilderAuthCompleted() {
			// Embedded builder clears its own auth overlay.
		},
		onBuilderAuthFailed(payload) {
			toast.error(payload && payload.message ? payload.message : 'Unable to sign you in to forms.');
		},
		async onBuilderSaved(payload) {
			await this.listForms();
			toast.success(payload && payload.name ? `${payload.name} saved` : 'Form saved');
		},
		async onBuilderPublished(payload) {
			this.closeBuilder();
		}
	},
	async created() {
		await this.$store.dispatch('setPageTitle', 'Forms');
		this.listForms();
	}
};
</script>

<style scoped></style>
